import { Signal, type SignalProps } from '@latitude/signal';
import { Spinner } from '@latitude/spinner';
import { cn } from '@latitude/token-utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { getSpinnerSize } from 'helpers';
import type { getSpinnerVariant } from 'helpers';
import { isValidElement } from 'react';
import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react';
import type { ButtonSize } from 'types';

import { iconsVariants } from './variants';

type ButtonAsChildProps = Omit<ComponentProps<typeof Slot>, 'children'> & {
  // A Slot target may render a non-button element, so this stays optional and
  // is only forwarded when the consumer explicitly asked for it.
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  children: ReactNode;
  loading: boolean;
  leadingElement?: ReactNode;
  trailingElement?: ReactNode;
  size: ButtonSize;
  spinnerVariant: ReturnType<typeof getSpinnerVariant>;
  signal?: SignalProps;
};

/**
 * Button's `asChild` (Radix `Slot`) path, split out from the plain <button>
 * path because Slot merges its props onto whatever `Slottable` wraps, and
 * only finds the `Slottable` marker among its OWN direct children — it can't
 * see through an intermediate component boundary. So this component must be
 * the one calling <Slot> directly rather than being rendered as Slot's child.
 *
 * Non-element children (e.g. a bare string) aren't valid Slot targets, so
 * they're auto-wrapped in a <span> to stay a valid merge target.
 */
export const ButtonAsChild = ({
  children,
  loading,
  leadingElement,
  trailingElement,
  size,
  spinnerVariant,
  signal,
  ...slotProps
}: ButtonAsChildProps) => (
  <Slot {...slotProps}>
    {loading && (
      <Spinner
        size={getSpinnerSize(size)}
        className="pointer-events-none absolute m-auto"
        variant={spinnerVariant}
      />
    )}
    {leadingElement && (
      <span
        className={cn('pointer-events-none', iconsVariants({ size, loading }))}
      >
        {leadingElement}
      </span>
    )}
    <Slottable>
      {isValidElement(children) ? children : <span>{children}</span>}
    </Slottable>
    {trailingElement && (
      <span
        className={cn('pointer-events-none', iconsVariants({ size, loading }))}
      >
        {trailingElement}
      </span>
    )}
    {signal && (
      <Signal
        {...signal}
        border
        className={cn([
          'pointer-events-none',
          'absolute',
          '-right-sizing-sys-size-4',
          '-top-sizing-sys-size-4',
        ])}
      />
    )}
  </Slot>
);
